import type { SocialPlatformData, ConnectionStatus, SocialConnection } from '@/mocks/superAdminSocialStatus';
import { statusMeta } from '@/mocks/superAdminSocialStatus';

interface Props {
  platform: SocialPlatformData;
  onView: (platform: SocialPlatformData) => void;
  onRenew: (platform: SocialPlatformData, connection: SocialConnection) => void;
  onReconnect: (platform: SocialPlatformData, connection: SocialConnection) => void;
  onHistory: (platform: SocialPlatformData, connection: SocialConnection) => void;
}

function countByStatus(connections: SocialConnection[], status: ConnectionStatus): number {
  return connections.filter((c) => c.status === status).length;
}

const actionBtnClass = 'flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold cursor-pointer whitespace-nowrap transition-colors';

export default function PlatformCard({ platform, onView, onRenew, onReconnect, onHistory }: Props) {
  const total = platform.connections.length;
  const connected = countByStatus(platform.connections, 'connected');
  const errors = countByStatus(platform.connections, 'error');
  const pending = countByStatus(platform.connections, 'pending');
  const expired = countByStatus(platform.connections, 'expired');

  const statusCounts = [
    { status: 'connected' as ConnectionStatus, value: connected },
    { status: 'error' as ConnectionStatus, value: errors },
    { status: 'pending' as ConnectionStatus, value: pending },
    { status: 'expired' as ConnectionStatus, value: expired },
  ];

  // Pick first connection with non-connected status for quick actions, or first one
  const firstIssue = platform.connections.find((c) => c.status !== 'connected');
  const firstConn = platform.connections[0];

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 hover:border-background-300/60 transition-colors group">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-white"
            style={{ backgroundColor: platform.color }}
          >
            <i className={`${platform.icon} text-lg`}></i>
          </div>
          <div>
            <p className="text-foreground-900 text-sm font-bold">{platform.label}</p>
            <p className="text-foreground-400 text-[10px]">{total} radios vinculadas</p>
          </div>
        </div>
        {connected === total && total > 0 ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Todo en orden
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[10px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
            {errors + pending + expired} requieren atención
          </span>
        )}
      </div>

      {/* Status grid */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {statusCounts.map((sc) => {
          const meta = statusMeta[sc.status];
          return (
            <div key={sc.status} className={`${meta.bg} rounded-xl p-2.5 text-center`}>
              <p className={`${meta.text} text-lg font-bold leading-none`}>{sc.value}</p>
              <p className={`${meta.text} text-[9px] font-medium mt-1 opacity-70`}>{meta.label}</p>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => onView(platform)}
          className={`${actionBtnClass} bg-background-100 text-foreground-600 hover:bg-background-200`}
        >
          <i className="ri-eye-line text-xs"></i>
          Ver
        </button>
        <button
          onClick={() => onRenew(platform, firstConn)}
          className={`${actionBtnClass} bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
        >
          <i className="ri-refresh-line text-xs"></i>
          Renovar
        </button>
        <button
          onClick={() => onReconnect(platform, firstIssue || firstConn)}
          className={`${actionBtnClass} bg-amber-50 text-amber-700 hover:bg-amber-100`}
        >
          <i className="ri-plug-line text-xs"></i>
          Reconectar
        </button>
        <button
          onClick={() => onHistory(platform, firstConn)}
          className={`${actionBtnClass} bg-slate-50 text-slate-600 hover:bg-slate-100`}
        >
          <i className="ri-history-line text-xs"></i>
          Historial
        </button>
      </div>
    </div>
  );
}